import React, { useState } from 'react';
// @ts-ignore
import styles from './ManagerCalendarViewSettings.module.scss';
import FormWrapper from '../../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import InputField from '../../../../../../Components/Form/components/InputField';
import Text from '../../../../../../Components/Base/Text/Text';
import RadioListField from '../../../../../../Components/Form/components/RadioListField';
import SelectField from '../../../../../../Components/Form/components/SelectField';
import { SelectValue } from '../../../../../../Components/Base/Select/Select';
import ManagerSettingsTable from '../../Base/ManagerSettingsTable/ManagerSettingsTable';
import ManagerSettingsTableItem from '../../Base/ManagerSettingsTable/ManagerSettingsTableItem';
import { CalendarViewIntervalEnum } from '../../../../../Calendar/Enums/CalendarViewInterval.enum';
import FormContainer from '../../../../../../Components/Form/FormContainer';
import { useManagerContext } from '../../../../Services/ManagerContext';
import { useEffectAsync } from '../../../../../../helpers';
import Loading from '../../../../../../Components/Loading';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../../Hooks/useTranslation';
import useCustomSettings from '../../../../../Base/Hooks/useCustomSettings';
import { CustomSettingKey } from '../../../../../Base/Enums/CustomSettingKey.enum';
import { CalendarView } from '../../../../../Calendar/Enums/CalendarView.eunm';
import FloatingSubmitButton from '../../../../../Base/Components/FloatingSubmitButton/FloatingSubmitButton';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppContext } from '../../../../../../Services/AppContext';
import SubmitButton from '../../../../../../Components/Button/SubmitButton';

const RADIO_OPTIONS = [
	{
		title: 'base.yes',
		value: true,
	},
	{
		title: 'base.no',
		value: false,
	},
];

const DEFAULT_VIEW_OPTIONS: SelectValue[] = [
	{
		label: 'component.managerCalendarViewSettings.defaultViewOptions.day',
		value: CalendarView.TIME_GRID_DAY,
	},
	{
		label: 'component.managerCalendarViewSettings.defaultViewOptions.week',
		value: CalendarView.TIME_GRID_WEEK,
	},
	{
		label: 'component.managerCalendarViewSettings.defaultViewOptions.month',
		value: CalendarView.DAY_GRID_MONTH,
	},
];

export const DEFAULT_SLOT_DURATION_OPTIONS: SelectValue[] = [
	{
		label: 'component.calendarViewSettings.slotDuration.per15minutes',
		value: CalendarViewIntervalEnum.FIFTEEN_MINUTES,
	},
	{
		label: 'component.calendarViewSettings.slotDuration.per30minutes',
		value: CalendarViewIntervalEnum.THIRTY_MINUTES,
	},
	{
		label: 'component.calendarViewSettings.slotDuration.per20minutes',
		value: CalendarViewIntervalEnum.TWENTY_MINUTES,
	},
	{
		label: 'component.calendarViewSettings.slotDuration.per1hour',
		value: CalendarViewIntervalEnum.ONE_HOUR,
	},
	{
		label: 'component.calendarViewSettings.slotDuration.per2hours',
		value: CalendarViewIntervalEnum.TWO_HOURS,
	},
];

interface Props {
	userId?: string;
	onSubmit?: () => void;
}

const ManagerCalendarViewSettings: React.FC<Props> = ({ userId, onSubmit }) => {
	const [isLoading, setIsLoading] = useState(false);
	const { workplaceId: workplaceIdAppContext } = useAppContext();
	const { workplaceId: workplaceIdManagerContext } = useManagerContext();
	const workplaceId = workplaceIdManagerContext || workplaceIdAppContext;
	const { getCustomSettings, saveCustomSettings } = useCustomSettings(
		workplaceId,
		userId,
	);
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();

	const hourStringSchema = yup
		.string()
		.nullable()
		.notRequired()
		.test(
			'is-empty-or-hour',
			t('validation.managerCalendarViewSettings.calendarViewTimeValueOptions'),
			(value?: string | null) => {
				if (!value) return true;
				if (!/^\d{1,2}$/.test(value)) return false;
				const num = Number(value);
				return num >= 0 && num <= 24;
			},
		);

	const isEmpty = (v: string | null | undefined): boolean =>
		!v || v.trim() === '';
	const isValidHour = (v: string | null | undefined): boolean => {
		if (!v) return false;
		const num = Number(v);
		return /^\d{1,2}$/.test(v) && num >= 0 && num <= 24;
	};

	type CalendarViewTimeType = {
		showCalendarFrom?: string | null;
		showCalendarTo?: string | null;
	};

	const calendarViewTimeSchema = yup
		.object({
			showCalendarFrom: hourStringSchema,
			showCalendarTo: hourStringSchema,
		})
		.test(
			'both-or-none',
			t('validation.managerCalendarViewSettings.calendarViewTimeBothOrNone'),
			(value: CalendarViewTimeType | undefined) => {
				if (!value) return true;
				const { showCalendarFrom: from, showCalendarTo: to } = value;
				return (
					(isEmpty(from) && isEmpty(to)) || (!isEmpty(from) && !isEmpty(to))
				);
			},
		)
		.test(
			'to-greater-than-from',
			t(
				'validation.managerCalendarViewSettings.calendarViewTimeGreaterThatFrom',
			),
			(value: CalendarViewTimeType | undefined) => {
				if (!value) return true;
				const { showCalendarFrom: from, showCalendarTo: to } = value;

				if (!isValidHour(from) || !isValidHour(to)) return true;

				return Number(to) > Number(from);
			},
		);

	const validationSchema = yup.object({
		calendarViewTime: calendarViewTimeSchema,
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	const handleLoad = async () => {
		const data = await getCustomSettings(CustomSettingKey.CALENDAR_SETTINGS);
		if (data) {
			formMethods.reset(data);
		}
	};

	useEffectAsync(async () => {
		setIsLoading(true);
		await handleLoad();
		setIsLoading(false);
	}, []);

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		const response = await saveCustomSettings(
			CustomSettingKey.CALENDAR_SETTINGS,
			values,
		);
		if (response?.status === 200) {
			showNotificationSuccess({ content: t('base.saved') });
			await handleLoad();
			onSubmit?.();
		}
		setIsLoading(false);
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<FormWrapper formMethods={formMethods}>
				<ManagerSettingsTable>
					<ManagerSettingsTableItem label='component.managerCalendarViewSettings.showWeekends'>
						<RadioListField name='showWeekends' inLine data={RADIO_OPTIONS} />
					</ManagerSettingsTableItem>
					<ManagerSettingsTableItem label='component.managerCalendarViewSettings.calendarView'>
						<FormContainer name='calendarViewTime'>
							<div className={styles.inline}>
								<Text
									tx='component.managerCalendarViewSettings.fromHours'
									className={styles.suffix}
								/>
								<div className={styles.daysWrapper}>
									<InputField
										name='showCalendarFrom'
										style={{ width: '100%' }}
									/>
								</div>
								<Text
									tx='component.managerCalendarViewSettings.toHours'
									className={styles.suffix}
								/>
								<div className={styles.daysWrapper}>
									<InputField name='showCalendarTo' style={{ width: '100%' }} />
								</div>
							</div>
						</FormContainer>
					</ManagerSettingsTableItem>
					<ManagerSettingsTableItem label='component.managerCalendarViewSettings.underColorWorkingHours'>
						<RadioListField
							name='highlightWorkingTime'
							inLine
							data={RADIO_OPTIONS}
						/>
					</ManagerSettingsTableItem>
					<ManagerSettingsTableItem label='component.managerCalendarViewSettings.defaultView'>
						<SelectField name='defaultView' options={DEFAULT_VIEW_OPTIONS} />
					</ManagerSettingsTableItem>
					<ManagerSettingsTableItem label='component.managerCalendarViewSettings.showShortEventsAsThirtyMinutes'>
						<RadioListField
							name='forceMinimumEventHeight'
							inLine
							data={RADIO_OPTIONS}
						/>
					</ManagerSettingsTableItem>
					<ManagerSettingsTableItem label='component.managerCalendarViewSettings.defaultSlotDurationDay'>
						<SelectField
							name='defaultSlotDurationDay'
							options={DEFAULT_SLOT_DURATION_OPTIONS}
							portal
							isFixed
						/>
					</ManagerSettingsTableItem>
					<ManagerSettingsTableItem label='component.managerCalendarViewSettings.defaultSlotDurationWeek'>
						<SelectField
							name='defaultSlotDurationWeek'
							options={DEFAULT_SLOT_DURATION_OPTIONS}
							portal
							isFixed
						/>
					</ManagerSettingsTableItem>
				</ManagerSettingsTable>
				{userId ? (
					<div className='my-3 px-3'>
						<SubmitButton onSubmit={handleSubmit} title='base.shared.save' />
					</div>
				) : (
					<FloatingSubmitButton
						onSubmit={handleSubmit}
						title='base.shared.save'
					/>
				)}
			</FormWrapper>
		</div>
	);
};

export default ManagerCalendarViewSettings;
